const FOOTER_ID = 'dynamic-footer';
const SECTION_ID = 'section2';
const CONTENT_ID = 'lp-content';
const PADDING_FOOTER_ID = 'padding-footer';
const APPS_FLYER_ONELINK_CTA_SELECTOR = '[data-apps-flyer-onelink-cta="true"]';
const APPS_FLYER_ONELINK_CTA_REFRESH_EVENT = 'spojob:apps-flyer-onelink-ctas:refresh';
const APPS_FLYER_INCOMING_PARAMS_KEY = 'ss_incoming_params';
const APPS_FLYER_WEB_REFERRER_KEY = 'ss_webReferrer';
const APPS_FLYER_SMART_SCRIPT_VERSION = '2_10_0';
const APPS_FLYER_INCOMING_PARAMS_TTL_MS = 2 * 60 * 60 * 1000;
const APPS_FLYER_CLICK_ID_KEYS = ['gclid', 'fbclid', 'twclid', 'sccid', 'ttclid', 'gbraid', 'wbraid'] as const;
const APPS_FLYER_DEFAULT_DEEP_LINK_VALUE = 'offers';
const APPS_FLYER_ALLOWED_DEEP_LINK_HOSTS = ['spojob.jp', 'adm.spojob.jp'] as const;
const APPS_FLYER_ALLOWED_DEEP_LINK_HOST_SUFFIXES = ['.spojob.click'] as const;
const SHOW_TRIGGER_OFFSET_PX = 140;
const OBSERVER_ROOT_MARGIN = `-${SHOW_TRIGGER_OFFSET_PX}px 0px 0px 0px`;

const HIDDEN_CLASSES = ['opacity-0', 'translate-y-[calc(100%+24px)]', 'pointer-events-none'] as const;
const VISIBLE_CLASSES = ['opacity-100', 'translate-y-0', 'pointer-events-auto'] as const;

function debugLog(...messages: unknown[]): void {
  if (!['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return;
  }

  console.debug('[worker_home_dynamic_footer]', ...messages);
}

type Nullable<T> = T | null;

type IncomingAppsFlyerParams = {
  af_ss_exp_at: number;
  [key: string]: string | number;
};

let observer: Nullable<IntersectionObserver> = null;
let footerElement: Nullable<HTMLElement> = null;
let contentElement: Nullable<HTMLElement> = null;
let paddingFooterElement: Nullable<HTMLElement> = null;
let resizeHandler: Nullable<() => void> = null;
let hasPassedSection = false;
let cachedFooterHeight: Nullable<number> = null;
let appsFlyerOneLinkClickURL: Nullable<string> = null;

function disconnectObserver(): void {
  if (!observer) {
    return;
  }

  debugLog('disconnectObserver');
  observer.disconnect();
  observer = null;
}

function applyClasses(element: HTMLElement, classes: readonly string[], action: 'add' | 'remove'): void {
  classes.forEach(className => {
    if (action === 'add') {
      element.classList.add(className);
    } else {
      element.classList.remove(className);
    }
  });
}

function hideFooter(element: HTMLElement): void {
  debugLog('hideFooter');
  element.dataset.state = 'hidden';
  element.setAttribute('aria-hidden', 'true');
  applyClasses(element, VISIBLE_CLASSES, 'remove');
  applyClasses(element, HIDDEN_CLASSES, 'add');
  applyContentPadding(0);
  unregisterResizeListener();
  applyPaddingFooterTransform(0);
}

function showFooter(element: HTMLElement): void {
  if (element.dataset.state === 'visible') {
    debugLog('showFooter skipped (already visible)');
    return;
  }

  debugLog('showFooter apply');
  ensureFooterMeasurement(element);
  element.dataset.state = 'visible';
  element.setAttribute('aria-hidden', 'false');
  applyClasses(element, HIDDEN_CLASSES, 'remove');
  applyClasses(element, VISIBLE_CLASSES, 'add');
  schedulePaddingSync();
  registerResizeListener();
  const padding = cachedFooterHeight ?? element.getBoundingClientRect().height;
  applyPaddingFooterTransform(padding);
}

function applyContentPadding(padding: number): void {
  if (!contentElement) {
    return;
  }

  contentElement.style.paddingBottom = padding > 0 ? `${padding}px` : '';
}

function applyPaddingFooterTransform(padding: number): void {
  if (paddingFooterElement) {
    paddingFooterElement.style.height = padding > 0 ? `${padding}px` : '0px';
    paddingFooterElement.style.transform = padding > 0 ? `translateY(${padding}px)` : '';
  }
}

function syncPadding(): void {
  if (!footerElement || footerElement.dataset.state !== 'visible') {
    return;
  }

  const { height } = footerElement.getBoundingClientRect();
  cachedFooterHeight = height;
  applyContentPadding(height);
  applyPaddingFooterTransform(height);
}

function schedulePaddingSync(): void {
  requestAnimationFrame(() => syncPadding());
}

function registerResizeListener(): void {
  if (resizeHandler) {
    return;
  }

  resizeHandler = () => {
    syncPadding();
  };

  window.addEventListener('resize', resizeHandler);
}

function unregisterResizeListener(): void {
  if (!resizeHandler) {
    return;
  }

  window.removeEventListener('resize', resizeHandler);
  resizeHandler = null;
}

function measureFooterHeight(element: HTMLElement): void {
  debugLog('measureFooterHeight start');
  const originalState = element.dataset.state;
  const originalInlineTransition = element.style.transition;
  const originalVisibility = element.style.visibility;

  element.dataset.state = 'measuring';
  element.style.transition = 'none';
  element.style.visibility = 'hidden';

  applyClasses(element, HIDDEN_CLASSES, 'remove');
  applyClasses(element, VISIBLE_CLASSES, 'add');

  const { height } = element.getBoundingClientRect();
  cachedFooterHeight = height;
  element.dataset.measuredHeight = `${height}`;
  debugLog('measureFooterHeight result', { height });

  applyClasses(element, VISIBLE_CLASSES, 'remove');
  applyClasses(element, HIDDEN_CLASSES, 'add');

  if (originalInlineTransition) {
    element.style.transition = originalInlineTransition;
  } else {
    element.style.removeProperty('transition');
  }

  if (originalVisibility) {
    element.style.visibility = originalVisibility;
  } else {
    element.style.removeProperty('visibility');
  }

  if (originalState) {
    element.dataset.state = originalState;
  } else {
    delete element.dataset.state;
  }
}

function ensureFooterMeasurement(element: HTMLElement): void {
  if (cachedFooterHeight !== null) {
    return;
  }

  measureFooterHeight(element);
}

function updateAppsFlyerOneLinkCtas(clickURL: string): void {
  document.querySelectorAll<HTMLAnchorElement>(APPS_FLYER_ONELINK_CTA_SELECTOR).forEach(link => {
    link.href = clickURL;
  });
}

function readIncomingAppsFlyerParams(): IncomingAppsFlyerParams[] {
  return JSON.parse(localStorage.getItem(APPS_FLYER_INCOMING_PARAMS_KEY) || '[]') as IncomingAppsFlyerParams[];
}

function writeIncomingAppsFlyerParams(params: IncomingAppsFlyerParams[]): void {
  localStorage.setItem(APPS_FLYER_INCOMING_PARAMS_KEY, JSON.stringify(params));
}

function removeExpiredAppsFlyerIncomingParams(now: number = Date.now()): void {
  writeIncomingAppsFlyerParams(readIncomingAppsFlyerParams().filter(params => params.af_ss_exp_at > now));
}

function preserveCurrentAppsFlyerIncomingParams(): void {
  const url = new URL(window.location.href);
  const params = Object.fromEntries(url.searchParams.entries());
  if (Object.keys(params).length === 0) {
    return;
  }

  writeIncomingAppsFlyerParams([
    {
      ...params,
      af_ss_exp_at: Date.now() + APPS_FLYER_INCOMING_PARAMS_TTL_MS,
    },
    ...readIncomingAppsFlyerParams(),
  ]);
}

function preserveAppsFlyerWebReferrer(): void {
  if (!document.referrer) {
    return;
  }

  const currentUrl = new URL(window.location.href);
  const referrerUrl = new URL(document.referrer);
  if (currentUrl.origin === referrerUrl.origin) {
    return;
  }

  localStorage.setItem(APPS_FLYER_WEB_REFERRER_KEY, JSON.stringify(document.referrer));
}

function findIncomingAppsFlyerParams(keys: string[]): IncomingAppsFlyerParams {
  return readIncomingAppsFlyerParams().find(params => keys.some(key => params[key])) || ({} as IncomingAppsFlyerParams);
}

function firstParamValue(params: IncomingAppsFlyerParams, keys: string[], defaultValue = ''): string {
  const key = keys.find(candidate => params[candidate]);
  if (!key) {
    return defaultValue;
  }

  const value = params[key];
  return typeof value === 'string' ? value : defaultValue;
}

function appendIfPresent(url: URL, key: string, value: string): void {
  if (!value) {
    return;
  }

  url.searchParams.set(key, value);
}

function isAppsFlyerAllowedDeepLinkHost(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase();
  const currentHostname = window.location.hostname.toLowerCase();
  if (normalizedHostname === currentHostname) {
    return true;
  }

  if (APPS_FLYER_ALLOWED_DEEP_LINK_HOSTS.some(host => host === normalizedHostname)) {
    return true;
  }

  return APPS_FLYER_ALLOWED_DEEP_LINK_HOST_SUFFIXES.some(suffix => normalizedHostname.endsWith(suffix));
}

function normalizedAppsFlyerAbsoluteDeepLinkValue(value: string): Nullable<string> {
  const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(value);
  const isProtocolRelative = value.startsWith('//');
  if (!hasScheme && !isProtocolRelative) {
    return null;
  }

  try {
    const url = isProtocolRelative ? new URL(`${window.location.protocol}${value}`) : new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return APPS_FLYER_DEFAULT_DEEP_LINK_VALUE;
    }

    if (!isAppsFlyerAllowedDeepLinkHost(url.hostname)) {
      return APPS_FLYER_DEFAULT_DEEP_LINK_VALUE;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return APPS_FLYER_DEFAULT_DEEP_LINK_VALUE;
  }
}

function appsFlyerDeepLinkValue(params: IncomingAppsFlyerParams, keys: string[]): string {
  const value = firstParamValue(params, keys, APPS_FLYER_DEFAULT_DEEP_LINK_VALUE).trim();
  if (!value) {
    return APPS_FLYER_DEFAULT_DEEP_LINK_VALUE;
  }

  return normalizedAppsFlyerAbsoluteDeepLinkValue(value) || value;
}

function generatedAppsFlyerOneLinkURL(oneLinkURL: string): string {
  removeExpiredAppsFlyerIncomingParams();
  preserveCurrentAppsFlyerIncomingParams();
  preserveAppsFlyerWebReferrer();

  const sourceKeys = ['utm_source'];
  const campaignKeys = ['utm_campaign'];
  const adSetKeys = ['utm_content'];
  const deepLinkValueKeys = ['sale_page'];
  const incomingParams = findIncomingAppsFlyerParams([
    ...sourceKeys,
    ...campaignKeys,
    ...adSetKeys,
    ...deepLinkValueKeys,
  ]);
  const currentURLParams = new URLSearchParams(window.location.search);
  const url = new URL(oneLinkURL);

  appendIfPresent(url, 'af_js_web', 'true');
  appendIfPresent(url, 'af_ss_ver', APPS_FLYER_SMART_SCRIPT_VERSION);
  appendIfPresent(url, 'pid', firstParamValue(incomingParams, sourceKeys));
  appendIfPresent(url, 'c', firstParamValue(incomingParams, campaignKeys));
  appendIfPresent(url, 'af_adset', firstParamValue(incomingParams, adSetKeys));
  appendIfPresent(url, 'deep_link_value', appsFlyerDeepLinkValue(incomingParams, deepLinkValueKeys));
  appendIfPresent(url, 'af_ss_ui', 'true');

  const referrer = JSON.parse(localStorage.getItem(APPS_FLYER_WEB_REFERRER_KEY) || 'null') as string | null;
  appendIfPresent(url, 'af_sub1', referrer || '');

  APPS_FLYER_CLICK_ID_KEYS.forEach(key => {
    appendIfPresent(url, key, currentURLParams.get(key) || '');
  });

  return url.toString();
}

function hasCurrentAppsFlyerMediaSource(): boolean {
  return Boolean(new URLSearchParams(window.location.search).get('utm_source'));
}

function refreshAppsFlyerOneLinkCtas(): void {
  if (!appsFlyerOneLinkClickURL) {
    return;
  }

  updateAppsFlyerOneLinkCtas(appsFlyerOneLinkClickURL);
}

function initializeAppsFlyerOneLink(): void {
  appsFlyerOneLinkClickURL = null;
  const oneLinkURL = contentElement?.dataset.appsFlyerOneLinkUrl;
  if (!oneLinkURL || !hasCurrentAppsFlyerMediaSource()) {
    return;
  }

  appsFlyerOneLinkClickURL = generatedAppsFlyerOneLinkURL(oneLinkURL);
  refreshAppsFlyerOneLinkCtas();
}

function createObserver(section: HTMLElement, footer: HTMLElement): void {
  debugLog('createObserver start');
  if (!('IntersectionObserver' in window)) {
    debugLog('IntersectionObserver unavailable, fallback to showFooter');
    requestAnimationFrame(() => showFooter(footer));
    return;
  }

  disconnectObserver();

  observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.target !== section) {
        return;
      }

      const sectionTop = entry.boundingClientRect.top;
      const hasPassedTriggerLine = sectionTop <= -SHOW_TRIGGER_OFFSET_PX;
      const hasPassedSectionTop = sectionTop <= 0;
      debugLog('observer entry', {
        isIntersecting: entry.isIntersecting,
        boundingTop: sectionTop,
        boundingBottom: entry.boundingClientRect.bottom,
        hasPassedSectionTop,
        hasPassedTriggerLine,
      });
      if (hasPassedTriggerLine || (!entry.isIntersecting && hasPassedSectionTop)) {
        if (!hasPassedSection) {
          hasPassedSection = true;
        }

        showFooter(footer);
        return;
      }

      if (entry.isIntersecting && sectionTop >= -SHOW_TRIGGER_OFFSET_PX) {
        hasPassedSection = false;
        hideFooter(footer);
      }
    });
  }, {
    threshold: [0],
    rootMargin: OBSERVER_ROOT_MARGIN,
  });

  observer.observe(section);
}

function initialize(): void {
  debugLog('initialize start');
  footerElement = document.getElementById(FOOTER_ID);
  const sectionElement = document.getElementById(SECTION_ID);
  contentElement = document.getElementById(CONTENT_ID);
  paddingFooterElement = document.getElementById(PADDING_FOOTER_ID);

  if (!footerElement || !sectionElement || !contentElement || !paddingFooterElement) {
    debugLog('initialize aborted', {
      hasFooter: Boolean(footerElement),
      hasSection: Boolean(sectionElement),
      hasContent: Boolean(contentElement),
      hasPaddingFooter: Boolean(paddingFooterElement),
    });
    disconnectObserver();
    return;
  }

  debugLog('initialize success, setting observer');
  hasPassedSection = false;
  cachedFooterHeight = null;
  initializeAppsFlyerOneLink();
  ensureFooterMeasurement(footerElement);
  hideFooter(footerElement);
  createObserver(sectionElement, footerElement);
}

function handleBeforeCache(): void {
  debugLog('handleBeforeCache');
  if (!footerElement) {
    return;
  }

  hideFooter(footerElement);
  disconnectObserver();
  hasPassedSection = false;
  applyContentPadding(0);
  unregisterResizeListener();
  cachedFooterHeight = null;
  applyPaddingFooterTransform(0);
}

function onDomContentLoaded(): void {
  debugLog('DOMContentLoaded event');
  document.removeEventListener('DOMContentLoaded', onDomContentLoaded);
  initialize();
}

function onTurboFrameLoad(event: Event): void {
  debugLog('turbo:frame-load event', event);
  const frame = event.target;
  if (!(frame instanceof HTMLElement)) {
    return;
  }

  if (frame.id !== 'contents') {
    return;
  }

  initialize();
}

if (document.readyState === 'loading') {
  debugLog('document loading, wait for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', onDomContentLoaded);
} else {
  debugLog('document readyState complete, initialize immediately');
  requestAnimationFrame(() => initialize());
}

debugLog('register turbo listeners');
document.addEventListener('turbo:load', initialize);
document.addEventListener('turbo:frame-load', onTurboFrameLoad);
document.addEventListener('turbo:before-cache', handleBeforeCache);
document.addEventListener(APPS_FLYER_ONELINK_CTA_REFRESH_EVENT, refreshAppsFlyerOneLinkCtas);
